import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogContent,
  CircularProgress,
  Snackbar,
  Alert,
  Pagination,
  Stack
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  limit,
  startAfter,
  doc,
  getDoc
} from 'firebase/firestore';
import { AuthContext } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import SalesFilters from '../../components/Sales/SalesFilters';
import SaleDetails from '../../components/Sales/SaleDetails';
import { 
  getSourceLabel, 
  getPaymentStatusLabel, 
  getDeliveryStatusLabel,
  getPaymentStatusColor,
  getDeliveryStatusColor
} from '../../constants/salesConstants';

const SalesPage = () => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [lastVisible, setLastVisible] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const ITEMS_PER_PAGE = 20;
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Filtry
  const [filters, setFilters] = useState({
    search: '',
    employee: 'all',
    paymentStatus: 'all',
    deliveryStatus: 'all',
    source: 'all',
    depositStatus: 'all',
    dateFrom: '',
    dateTo: '',
    client: 'all'
  });

  useEffect(() => {
    fetchSales();
    if (isAdmin) {
      fetchEmployees();
    }
    fetchClients();
  }, [currentUser, isAdmin, filters, page]);

  const fetchEmployees = async () => {
    try {
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      const employeesList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEmployees(employeesList);
    } catch (error) {
      console.error('Błąd podczas pobierania pracowników:', error);
    }
  };

  const fetchClients = async () => {
    try {
      // Pobierz tylko podstawowe dane klientów (id, imię, nazwisko)
      // Jeśli użytkownik nie ma uprawnień, nie wyświetlaj błędu, tylko kontynuuj bez danych klientów
      const clientsQuery = query(collection(db, 'clients'), limit(100));
      const clientsSnapshot = await getDocs(clientsQuery);
      const clientsList = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        firstName: doc.data().firstName,
        lastName: doc.data().lastName
      }));
      setClients(clientsList);
    } catch (error) {
      console.error('Błąd podczas pobierania klientów:', error);
      // Nie wyświetlaj błędu użytkownikowi, po prostu kontynuuj bez danych klientów
    }
  };

  const fetchSales = async () => {
    try {
      setLoading(true);
      const salesRef = collection(db, 'sales');
      let salesQuery;

      // Budowanie podstawowego zapytania
      if (isAdmin) {
        // Administrator widzi wszystkie sprzedaże
        salesQuery = query(
          salesRef,
          orderBy('transactionDate', 'desc'),
          limit(ITEMS_PER_PAGE)
        );
      } else {
        // Zwykły pracownik widzi tylko swoje sprzedaże
        salesQuery = query(
          salesRef,
          where('employeeId', '==', currentUser.uid),
          orderBy('transactionDate', 'desc'),
          limit(ITEMS_PER_PAGE)
        );
      }

      // Dodaj paginację jeśli nie jest pierwsza strona
      if (page > 1 && lastVisible) {
        salesQuery = query(
          salesRef,
          ...(isAdmin ? [] : [where('employeeId', '==', currentUser.uid)]),
          orderBy('transactionDate', 'desc'),
          startAfter(lastVisible),
          limit(ITEMS_PER_PAGE)
        );
      }

      const salesSnapshot = await getDocs(salesQuery);
      
      // Zapisz ostatni widoczny dokument do paginacji
      if (!salesSnapshot.empty) {
        setLastVisible(salesSnapshot.docs[salesSnapshot.docs.length - 1]);
      }

      let salesList = await Promise.all(salesSnapshot.docs.map(async doc => {
        const saleData = {
          id: doc.id,
          ...doc.data(),
          transactionDate: doc.data().transactionDate?.toDate() || new Date(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          lastUpdate: doc.data().lastUpdate?.toDate() || new Date()
        };

        // Pobierz dane klienta
        if (saleData.clientId) {
          try {
            // Pobierz dane klienta z kolekcji najwyższego poziomu 'clients'
            const clientDoc = await getDoc(doc(db, 'clients', saleData.clientId));
            if (clientDoc.exists()) {
              saleData.clientName = `${clientDoc.data().firstName || ''} ${clientDoc.data().lastName || ''}`.trim();
            }
          } catch (error) {
            console.error('Błąd podczas pobierania danych klienta:', error);
          }
        }

        // Pobierz dane pracownika
        if (saleData.employeeId) {
          const employee = employees.find(emp => emp.id === saleData.employeeId);
          if (employee) {
            saleData.employeeName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
          }
        }

        return saleData;
      }));

      // Zastosuj filtry
      salesList = applySalesFilters(salesList);
      
      setSales(salesList);
      
      // Pobierz całkowitą liczbę stron (uproszczone podejście)
      // W rzeczywistej aplikacji możesz potrzebować bardziej zaawansowanej logiki paginacji
      if (page === 1) {
        const countQuery = query(
          salesRef,
          ...(isAdmin ? [] : [where('employeeId', '==', currentUser.uid)])
        );
        const countSnapshot = await getDocs(countQuery);
        setTotalPages(Math.ceil(countSnapshot.size / ITEMS_PER_PAGE));
      }
    } catch (error) {
      console.error('Błąd podczas pobierania sprzedaży:', error);
      setSnackbar({
        open: true,
        message: 'Błąd podczas pobierania danych sprzedaży',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const applySalesFilters = (salesList) => {
    return salesList.filter(sale => {
      // Filtr wyszukiwania
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const invoiceNumber = (sale.invoiceNumber || '').toLowerCase();
        const notes = (sale.notes || '').toLowerCase();
        
        if (!invoiceNumber.includes(searchTerm) && !notes.includes(searchTerm)) {
          return false;
        }
      }

      // Filtr pracownika
      if (isAdmin && filters.employee !== 'all' && sale.employeeId !== filters.employee) {
        return false;
      }

      // Filtr statusu płatności
      if (filters.paymentStatus !== 'all' && sale.paymentStatus !== filters.paymentStatus) {
        return false;
      }

      // Filtr statusu dostawy
      if (filters.deliveryStatus !== 'all' && sale.deliveryStatus !== filters.deliveryStatus) {
        return false;
      }

      // Filtr źródła
      if (filters.source !== 'all' && sale.source !== filters.source) {
        return false;
      }

      // Filtr statusu zadatku
      if (filters.depositStatus !== 'all') {
        if (filters.depositStatus === 'has_deposit' && !sale.deposit) {
          return false;
        }
        if (filters.depositStatus === 'no_deposit' && sale.deposit) {
          return false;
        }
        if (filters.depositStatus !== 'has_deposit' && filters.depositStatus !== 'no_deposit' && 
            (!sale.deposit || sale.deposit.status !== filters.depositStatus)) {
          return false;
        }
      }

      // Filtr zakresu dat
      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        if (sale.transactionDate < fromDate) {
          return false;
        }
      }

      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // Koniec dnia
        if (sale.transactionDate > toDate) {
          return false;
        }
      }

      // Filtr klienta
      if (filters.client !== 'all' && sale.clientId !== filters.client) {
        return false;
      }

      return true;
    });
  };

  const handleViewSale = (sale) => {
    setSelectedSale(sale);
    setDetailsOpen(true);
  };

  const handleSaleUpdate = (updatedSale) => {
    setSales(sales.map(sale => 
      sale.id === updatedSale.id ? updatedSale : sale
    ));
    setSelectedSale(updatedSale);
    setSnackbar({
      open: true,
      message: 'Sprzedaż została zaktualizowana',
      severity: 'success'
    });
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const formatDate = (date) => {
    if (!date) return 'Brak daty';
    return format(date instanceof Date ? date : date.toDate(), 'dd.MM.yyyy', { locale: pl });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.firstName} ${client.lastName}` : clientId;
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.firstName || ''} ${employee.lastName || ''}`.trim() : employeeId;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Sprzedaże
      </Typography>

      <SalesFilters 
        filters={filters} 
        onFilterChange={setFilters} 
        employees={employees} 
        isAdmin={isAdmin}
      />

      {/* Tabela sprzedaży */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Data transakcji</TableCell>
              <TableCell>Klient</TableCell>
              {isAdmin && <TableCell>Sprzedawca</TableCell>}
              <TableCell>Źródło</TableCell>
              <TableCell align="right">Wartość</TableCell>
              <TableCell>Status płatności</TableCell>
              <TableCell>Status dostawy</TableCell>
              <TableCell>Zadatek</TableCell>
              <TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 10 : 9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : sales.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 10 : 9} align="center">
                  <Typography variant="body1" color="textSecondary">
                    Brak sprzedaży spełniających kryteria
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sales.map((sale) => (
                <TableRow key={sale.id} hover>
                  <TableCell>{sale.id.substring(0, 8)}...</TableCell>
                  <TableCell>{formatDate(sale.transactionDate)}</TableCell>
                  <TableCell>
                    {sale.clientName || getClientName(sale.clientId) || 'Brak danych'}
                  </TableCell>
                  {isAdmin && (
                    <TableCell>{sale.employeeName || getEmployeeName(sale.employeeId)}</TableCell>
                  )}
                  <TableCell>
                    <Chip 
                      label={getSourceLabel(sale.source)} 
                      color={sale.source === 'order' ? 'secondary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <strong>{formatPrice(sale.totalAmount || 0)}</strong>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getPaymentStatusLabel(sale.paymentStatus)} 
                      color={getPaymentStatusColor(sale.paymentStatus)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getDeliveryStatusLabel(sale.deliveryStatus)} 
                      color={getDeliveryStatusColor(sale.deliveryStatus)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {sale.deposit ? (
                      <Chip 
                        label={formatPrice(sale.deposit.amount)} 
                        color="primary"
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Brak
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Zobacz szczegóły">
                        <IconButton 
                          size="small"
                          onClick={() => handleViewSale(sale)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edytuj statusy">
                        <IconButton 
                          size="small"
                          onClick={() => handleViewSale(sale)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {sale.invoiceNumber && (
                        <Tooltip title="Faktura">
                          <IconButton 
                            size="small"
                            onClick={() => console.log('Pobierz fakturę', sale.invoiceNumber)}
                          >
                            <ReceiptIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginacja */}
      {totalPages > 1 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Dialog szczegółów sprzedaży */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent>
          {selectedSale && (
            <SaleDetails 
              sale={selectedSale} 
              onUpdate={handleSaleUpdate}
              onClose={() => setDetailsOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SalesPage;
